/**
 * Trade lanes (plan R17, §2.9 flow 6; spec §4.3; D-TBD 15). In normal flight, lane rings within `laneCheckRangeM`
 * are tested for a crossing of their disc on the tick's segment. An up ring crossed within `entryConeDeg` of the lane
 * direction (either way) below `entryMaxSpeed` starts a ride to the last ring before any down ring
 * (`laneExitOrder`); when the next ring is down, `REV.laneInterrupted` fires and there is no ride. The ride moves the
 * detached ship along the lane at the lane law's speed; a J tap moves the exit to the next ring ahead. Passing the exit
 * ring's plane puts the ship `exitOffsetM` past it at `exitSpeed` along the lane, attached. Lanes are never
 * interdicted.
 *
 * `checkLaneEntry` and `stepLane` run per tick and allocate nothing (D-014); entering and leaving are rare events.
 */
import { logger } from '../../core/logger'
import type { LaneRingSite, LaneSite, SystemLayout } from '../../galaxy/layout/types'
import { emitReach, REV } from '../../reach/events'
import type { ReachCore } from '../../reach/runtime/types'
import { laneRingUp } from '../../reach/state'
import { attachShip, detachShip, type Sim } from '../../sim'
import { Pos64, PrevPos64, Vel } from '../../world/components'
import { cruiseLagSFor, type TravelConfig } from '../config'
import { discCrossing, laneExitOrder } from '../math'
import { cancelCharge } from './transitions'
import { stepLaneShip } from './kinematics'
import { bandOfShip, shipFromEcs, shipToEcs, speedOf, turnDetached } from './flight'
import type { TravelRuntime } from './state'

const DEG = Math.PI / 180

/** Whether a crossing enters the lane: nose within `entryConeDeg` of `sign` × dir and speed below `entryMaxSpeed`. */
export function laneEntryGate(
  noseX: number,
  noseY: number,
  noseZ: number,
  speed: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  sign: number,
  cfg: TravelConfig,
): boolean {
  const l = cfg.lanes
  const along = (noseX * dirX + noseY * dirY + noseZ * dirZ) * sign
  return speed < l.entryMaxSpeed && along >= Math.cos(l.entryConeDeg * DEG)
}

/** The lane's ring with `order`, or undefined. */
export function ringAtOrder(layout: SystemLayout, lane: LaneSite, order: number): LaneRingSite | undefined {
  const ids = lane.ringIndices
  for (let i = 0; i < ids.length; i++) {
    const r = layout.laneRings[ids[i]]
    if (r !== undefined && r.order === order) return r
  }
  return undefined
}

/** Fills `rt.laneUp` by ring order with each ring's state; returns the ring count. */
function fillLaneUp(rt: TravelRuntime, core: ReachCore, layout: SystemLayout, lane: LaneSite): number {
  const ids = lane.ringIndices
  const up = rt.laneUp
  up.length = ids.length
  for (let i = 0; i < ids.length; i++) up[i] = false
  for (let i = 0; i < ids.length; i++) {
    const r = layout.laneRings[ids[i]]
    if (r !== undefined && r.order >= 0 && r.order < ids.length) up[r.order] = laneRingUp(core.state, r)
  }
  return ids.length
}

/** Starts a ride (rare): the ship detaches and rides toward `exit`. */
function startRide(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number, lane: LaneSite, entry: LaneRingSite, exit: LaneRingSite, sign: number): void {
  const t = core.travel
  if (t.mode === 'charging') cancelCharge(core, eid)
  const speed = speedOf(eid)
  detachShip(sim, eid)
  t.mode = 'lane'
  t.laneIndex = lane.index
  t.laneExitRing = exit.index
  t.speed = speed
  t.allowed = 0
  t.etaS = 0
  rt.laneSign = sign
  rt.laneDir.x = lane.dir[0] * sign
  rt.laneDir.y = lane.dir[1] * sign
  rt.laneDir.z = lane.dir[2] * sign
  emitReach(core.events, REV.laneEnter, lane.index, entry.index, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
}

/** In normal flight: the lane ring crossed this tick, if any, and whether it started a ride. Allocation-free. */
export function checkLaneEntry(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): boolean {
  const layout = rt.layout
  if (layout === null) return false
  const cfg = rt.configs.travel
  const range = rt.configs.runtime.laneCheckRangeM
  const rings = layout.laneRings
  const cx = Pos64.x[eid]
  const cy = Pos64.y[eid]
  const cz = Pos64.z[eid]
  for (let i = 0; i < rings.length; i++) {
    const r = rings[i]
    const dx = r.position[0] - cx
    const dy = r.position[1] - cy
    const dz = r.position[2] - cz
    if (dx * dx + dy * dy + dz * dz > range * range) continue
    const lane = layout.lanes[r.laneIndex]
    if (lane === undefined) continue
    const n = lane.dir
    const hit = discCrossing(PrevPos64.x[eid], PrevPos64.y[eid], PrevPos64.z[eid], cx, cy, cz, r.position[0], r.position[1], r.position[2], n[0], n[1], n[2], r.apertureM, rt.disc)
    if (!hit || !laneRingUp(core.state, r)) continue
    const sign = rt.disc.sign
    const nose = rt.nose
    if (!laneEntryGate(nose.x, nose.y, nose.z, speedOf(eid), n[0], n[1], n[2], sign, cfg)) continue
    const count = fillLaneUp(rt, core, layout, lane)
    const exitOrder = laneExitOrder(rt.laneUp, r.order, sign)
    if (exitOrder < 0) {
      const next = r.order + (sign < 0 ? -1 : 1)
      if (next >= 0 && next < count) emitReach(core.events, REV.laneInterrupted, lane.index, r.index, cx, cy, cz, 0)
      return false
    }
    const exit = ringAtOrder(layout, lane, exitOrder)
    if (exit === undefined) return false
    startRide(rt, core, sim, eid, lane, r, exit, sign)
    return true
  }
  return false
}

/** Ends the ride at `exit` (rare): `exitOffsetM` past its plane on the lane line, at `exitSpeed`, attached. */
function exitRide(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number, lane: LaneSite, exit: LaneRingSite): void {
  const t = core.travel
  const l = rt.configs.travel.lanes
  const s = rt.laneSign
  const dx = lane.dir[0] * s
  const dy = lane.dir[1] * s
  const dz = lane.dir[2] * s
  Pos64.x[eid] = exit.position[0] + dx * l.exitOffsetM
  Pos64.y[eid] = exit.position[1] + dy * l.exitOffsetM
  Pos64.z[eid] = exit.position[2] + dz * l.exitOffsetM
  Vel.x[eid] = dx * l.exitSpeed
  Vel.y[eid] = dy * l.exitSpeed
  Vel.z[eid] = dz * l.exitSpeed
  attachShip(sim, eid)
  t.mode = 'normal'
  t.speed = l.exitSpeed
  t.allowed = 0
  t.laneIndex = -1
  t.laneExitRing = -1
  emitReach(core.events, REV.laneExit, lane.index, exit.index, Pos64.x[eid], Pos64.y[eid], Pos64.z[eid], 0)
}

/** On a J tap: the exit moves to the nearest ring ahead of the ship, if it is before the current exit. */
function exitAtNextRing(rt: TravelRuntime, core: ReachCore, layout: SystemLayout, lane: LaneSite): void {
  const t = core.travel
  const s = rt.ship
  const d = rt.laneDir
  const ids = lane.ringIndices
  let best = -1
  let bestAlong = Infinity
  for (let i = 0; i < ids.length; i++) {
    const r = layout.laneRings[ids[i]]
    if (r === undefined) continue
    const along = (r.position[0] - s.x) * d.x + (r.position[1] - s.y) * d.y + (r.position[2] - s.z) * d.z
    if (along > 0 && along < bestAlong) {
      bestAlong = along
      best = r.index
    }
  }
  const exit = layout.laneRings[t.laneExitRing]
  if (best < 0 || exit === undefined) return
  const exitAlong = (exit.position[0] - s.x) * d.x + (exit.position[1] - s.y) * d.y + (exit.position[2] - s.z) * d.z
  if (bestAlong < exitAlong) t.laneExitRing = best
}

/** A ride whose lane or exit ring is not in the layout (a bug, logged): back to normal flight where the ship is. */
function abandonRide(core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  logger.error(`reach: lane ride ${t.laneIndex} → ring ${t.laneExitRing} is not in the layout; leaving the lane`)
  t.mode = 'normal'
  t.laneIndex = -1
  t.laneExitRing = -1
  attachShip(sim, eid)
}

/** One tick of a lane ride. Allocation-free until the exit. */
export function stepLane(rt: TravelRuntime, core: ReachCore, sim: Sim, eid: number): void {
  const t = core.travel
  const layout = rt.layout
  const lane = layout === null ? undefined : layout.lanes[t.laneIndex]
  if (layout === null || lane === undefined || layout.laneRings[t.laneExitRing] === undefined) {
    abandonRide(core, sim, eid)
    return
  }
  turnDetached(rt, sim, eid)
  const ship = rt.ship
  shipFromEcs(rt, eid)
  if (core.input.cruisePressed) exitAtNextRing(rt, core, layout, lane)
  const exit = layout.laneRings[t.laneExitRing]
  ship.speed = t.speed
  ship.throttle = t.throttle
  ship.lagS = cruiseLagSFor(rt.configs.travel, bandOfShip(sim, eid))
  stepLaneShip(ship, rt.laneDir, exit, rt.configs.travel)
  shipToEcs(rt, eid)
  t.speed = ship.speed
  t.allowed = ship.allowed
  t.etaS = 0
  if (ship.dTarget <= 0) exitRide(rt, core, sim, eid, lane, exit)
}
